// Renders subforms (tabs) inputs
// calls FormContainer handlers on input change
// Always renders item(element) tabs, renders others tabs depending on flavors of the item
import { useEffect, DependencyList } from 'react';
import { get, remove } from 'lodash';
import { useSetState } from 'react-use';
import { GridContainerFullHeight, Tabs } from '@ts/storybook';
import { addNewFlavorTabs } from './flavor.utils';

import { Icon } from '@ts/storybook';
import { CDevice, CElement, IForm } from '@ts/ui-models';
import { IAppProps } from '@interfaces/App.interface';

interface IItemFormProps extends IForm, IAppProps {}

const ItemForm = (props: IItemFormProps) => {
  const { values } = props;

  const [state, setState] = useSetState<{
    tabs: any[];
    selectedTab: number;
    readingsTableProps: any;
    showReadingsModal: boolean;
  }>({
    tabs: [],
    selectedTab: 0,
    readingsTableProps: null,
    showReadingsModal: false,
  });

  //#region Handlers
  const changeHandler = attrName => callbackArg => {
    let value = get(callbackArg, 'target.value', callbackArg);
    const currentTab: any = state.tabs[state.selectedTab];
    if (!currentTab) return null;

    const currentFlavor = currentTab.flavor;
    if (currentFlavor == 'element' || attrName == 'serialNumber') {
      // update in root of values
      props.onChange(attrName, value);
    } else {
      // update flavor object in values.flavors
      props.onChange(`flavors.${currentFlavor}.${attrName}`, value);
    }
  };

  const handleSelectTab = (event, value) => {
    setState({ selectedTab: value });
  };

  //#region Functions
  const getTabValues = () => {
    let rootValues = props.values;
    if (currentFlavor == 'element') return new CElement(rootValues);
    const flavorValues = get(rootValues, `flavors.${currentFlavor}`, {});
    let tabValues = { ...flavorValues, logicalId: rootValues.logicalId };
    if (currentFlavor == 'person') tabValues.images = rootValues.images;
    if (currentFlavor === 'device') {
      tabValues.itemState = rootValues.itemState;
      tabValues.serialNumber = rootValues.serialNumber;
      return new CDevice(tabValues);
    }
    return new CElement(tabValues);
  };

  const generateTabs = (tab, i) => {
    let iconComponent = tab.icon ? <Icon icon={tab.icon} /> : undefined;
    const disabled = ['generalImages', 'locationHistory'];
    return {
      value: i,
      label: tab.label,
      icon: iconComponent,
      disabled: disabled.includes(tab.id) && tab.fnDisabled && tab.fnDisabled(props),
    };
  };
  //#endregion

  //#region Effects
  const effectGenerateTabs = (deps: DependencyList) => {
    useEffect(() => {
      let tabs = addNewFlavorTabs('item', [], 'element');

      // Flavors tabs
      if (values && values.flavors) {
        Object.keys(values.flavors).map(flavor => {
          tabs = addNewFlavorTabs('item', tabs, `${flavor}`);
        });
      }

      tabs = addNewFlavorTabs('item', tabs, 'otherElement');

      // in mode new, remove auxiliary tabs
      if (props.mode == 'new') {
        remove(tabs, { isAux: true });
      }

      setState({
        tabs,
        selectedTab: state.selectedTab >= tabs.length ? 0 : state.selectedTab || 0,
      });
    }, deps);
  };
  //#endregion

  effectGenerateTabs([values.flavors]);

  const tabSelected = state.tabs[state.selectedTab];
  if (!tabSelected) return;
  const InnerForm = tabSelected.content;
  const currentFlavor = tabSelected.flavor;

  return (
    <GridContainerFullHeight id="item-form" spacing={2} mt={0}>
      <Tabs
        id="item-form-tabs"
        sticky={-17}
        justifyContent="flex-start"
        value={state.selectedTab}
        onChange={handleSelectTab}
        tabs={state.tabs.map((tab, i) => generateTabs(tab, i))}
      >
        <InnerForm
          app={props.app}
          data={props.data}
          mode={props.mode}
          values={getTabValues()}
          onChange={(attr, value) => changeHandler(attr)(value)}
          onCallbackChange={changeHandler}
          isItem
        />
      </Tabs>
    </GridContainerFullHeight>
  );
};

export default ItemForm;
